import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { User, Mail, Lock, LogOut, Shield, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import TwoFactorSettings from '../components/TwoFactorSettings';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

export default function Settings() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Déconnexion réussie');
    navigate('/');
  };

  const handleLogoutAll = async () => {
    if (!window.confirm('Fermer la session sur tous vos appareils, y compris celui-ci ?')) return;
    try {
      await axios.post(`${API_URL}/api/auth/logout-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Toutes les sessions ont été fermées');
    } catch {
      toast.error('Erreur, réessayez');
      return;
    }
    await logout();
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) return toast.error('Le nom ne peut pas être vide');
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`,
        { name: name.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profil mis à jour !');
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async () => {
    if (!currentPassword || !newPassword) return toast.error('Remplis tous les champs');
    if (newPassword !== confirmPassword) return toast.error('Les mots de passe ne correspondent pas');
    if (newPassword.length < 6) return toast.error('Le mot de passe doit faire au moins 6 caractères');
    setSavingPassword(true);
    try {
      await axios.put(`${API_URL}/api/auth/password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mot de passe mis à jour !');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mot de passe actuel incorrect');
    } finally {
      setSavingPassword(false);
    }
  };

  const plan = user?.subscription_plan || 'free';

  const getPlanLabel = () => {
    switch (plan) {
      case 'starter':    return 'Starter';
      case 'pro':        return 'Pro';
      case 'enterprise': return 'Enterprise';
      default:           return 'Gratuit';
    }
  };

  const getPlanDescription = () => {
    switch (plan) {
      case 'starter':    return '5 audits/mois';
      case 'pro':        return '30 audits/mois + Rapports IA';
      case 'enterprise': return 'Audits illimités + API + Export PDF';
      default:           return 'Aucun audit — souscrivez un plan pour commencer';
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white font-mono uppercase tracking-tighter">Paramètres</h1>
          <p className="text-zinc-500 text-xs font-mono mt-1 uppercase tracking-widest">Gérez votre compte</p>
        </div>

        {/* Profil */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-5">
          <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest border-l-2 border-blue-500 pl-3 flex items-center gap-2">
            <User className="w-3 h-3 text-blue-500" /> Profil
          </h2>

          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-sm bg-blue-500/20 border border-blue-500/20 flex items-center justify-center">
              <span className="text-blue-500 text-2xl font-black font-mono">
                {user?.name?.charAt(0).toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <div className="text-white font-mono font-bold">{user?.name}</div>
              <div className="text-zinc-500 text-xs font-mono">{user?.email}</div>
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t border-zinc-800">
            <div className="space-y-1">
              <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Nom</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full bg-black border border-zinc-800 p-3 pl-10 text-white font-mono text-sm focus:border-blue-500 outline-none transition-all"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input
                  value={user?.email || ''}
                  disabled
                  className="w-full bg-zinc-900 border border-zinc-800 p-3 pl-10 text-zinc-500 font-mono text-sm cursor-not-allowed"
                />
              </div>
            </div>

            <Button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="bg-blue-600 hover:bg-blue-700 font-mono rounded-none text-xs"
            >
              <Save className="w-3 h-3 mr-2" />
              {savingProfile ? 'Sauvegarde...' : 'Sauvegarder_Profil'}
            </Button>
          </div>
        </div>

        {/* Mot de passe */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-5">
          <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest border-l-2 border-yellow-500 pl-3 flex items-center gap-2">
            <Lock className="w-3 h-3 text-yellow-500" /> Changer le mot de passe
          </h2>

          <div className="space-y-3">
            {[
              { label: 'Mot de passe actuel', value: currentPassword, setter: setCurrentPassword, show: showCurrent, toggle: () => setShowCurrent(v => !v) },
              { label: 'Nouveau mot de passe', value: newPassword, setter: setNewPassword, show: showNew, toggle: () => setShowNew(v => !v) },
              { label: 'Confirmer le nouveau mot de passe', value: confirmPassword, setter: setConfirmPassword, show: showNew, toggle: null },
            ].map(({ label, value, setter, show, toggle }) => (
              <div key={label} className="space-y-1">
                <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">{label}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <input
                    type={show ? 'text' : 'password'}
                    value={value}
                    onChange={e => setter(e.target.value)}
                    className="w-full bg-black border border-zinc-800 p-3 pl-10 pr-10 text-white font-mono text-sm focus:border-blue-500 outline-none transition-all"
                  />
                  {toggle && (
                    <button type="button" onClick={toggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-white">
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            ))}

            <Button
              onClick={handleSavePassword}
              disabled={savingPassword}
              className="bg-yellow-600 hover:bg-yellow-700 font-mono rounded-none text-xs"
            >
              <Save className="w-3 h-3 mr-2" />
              {savingPassword ? 'Mise à jour...' : 'Changer_Mot_de_Passe'}
            </Button>
          </div>
        </div>

        {/* Double authentification */}
        <TwoFactorSettings />

        {/* Abonnement */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
          <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest border-l-2 border-green-500 pl-3 flex items-center gap-2 mb-4">
            <Shield className="w-3 h-3 text-green-500" /> Abonnement
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-mono font-bold uppercase">
                Plan {getPlanLabel()}
              </div>
              <div className={`text-xs font-mono mt-1 ${plan === 'free' ? 'text-red-400' : 'text-zinc-500'}`}>
                {getPlanDescription()}
              </div>
            </div>
            <Button
              onClick={() => navigate('/billing')}
              className={`font-mono rounded-none text-xs ${plan === 'free' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-zinc-700 hover:bg-zinc-600'}`}
            >
              {plan === 'free' ? 'Choisir un plan →' : 'Gérer'}
            </Button>
          </div>
        </div>

        {/* Zone danger */}
        <div className="bg-zinc-900 border border-red-500/20 rounded-sm p-6">
          <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest border-l-2 border-red-500 pl-3 mb-4">Zone de danger</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-white font-mono font-bold text-sm">Déconnexion</div>
                <div className="text-zinc-500 text-xs font-mono">Fermer la session sur cet appareil</div>
              </div>
              <Button onClick={handleLogout} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-500/30 font-mono rounded-none text-xs shrink-0">
                <LogOut className="w-3 h-3 mr-2" /> Déconnexion
              </Button>
            </div>
            <div className="flex items-center justify-between gap-4 pt-4 border-t border-zinc-800">
              <div>
                <div className="text-white font-mono font-bold text-sm">Tous les appareils</div>
                <div className="text-zinc-500 text-xs font-mono">Utile si vous pensez qu'une session est restée ouverte ailleurs</div>
              </div>
              <Button onClick={handleLogoutAll} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-500/30 font-mono rounded-none text-xs shrink-0">
                <LogOut className="w-3 h-3 mr-2" /> Tout déconnecter
              </Button>
            </div>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}
